import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation, useNavigate } from 'react-router-dom';
import { initDeepLinks } from './services/deepLinks.js';
import { initPushNotifications } from './services/pushNotifications.js';
import { Toaster } from 'react-hot-toast';
import './index.css';

import { AuthProvider, useAuth } from './context/AuthContext.jsx';
import { PWAProvider } from './context/PWAContext.jsx';
import { ThemeProvider } from './context/ThemeContext.jsx';
import AppLayout          from './components/shared/AppLayout.jsx';
import DeviceRouter       from './layouts/DeviceRouter.jsx';

// Public pages
import LoginPage          from './pages/LoginPage.jsx';
import RegisterPage       from './pages/RegisterPage.jsx';
import ForgotPasswordPage from './pages/ForgotPasswordPage.jsx';
import ResetPasswordPage  from './pages/ResetPasswordPage.jsx';
import InviteAcceptPage   from './pages/InviteAcceptPage.jsx';
// v2.5.97 — public RSVP result page reached after a one-click
// spare invite from email. No login required.
import RsvpResultPage     from './pages/RsvpResultPage.jsx';

// Protected pages
import DashboardPage  from './pages/DashboardPage.jsx';
// v2.5.146 — onboarding wizard for new users
import OnboardingWizardPage from './pages/OnboardingWizardPage.jsx';
import PlayersPage    from './pages/PlayersPage.jsx';
import SeasonsPage    from './pages/SeasonsPage.jsx';
import ImportPlayersPage  from './pages/ImportPlayersPage.jsx';
import SchedulePage        from './pages/SchedulePage.jsx';
import ImportSchedulePage  from './pages/ImportSchedulePage.jsx';
import GamePage       from './pages/GamePage.jsx';
import ChatPage       from './pages/ChatPage.jsx';
import TeamPage       from './pages/TeamPage.jsx';
import StatsPage      from './pages/StatsPage.jsx';
// May 4 2026 (v2.5.70) — player-scoped stats page. Distinct from
// StatsPage (coach-facing leaderboards/AI/feedback). Lives at
// /team/:teamId/my-stats and shows only the current user's data.
import MyStatsPage    from './pages/MyStatsPage.jsx';
import ScoreboardsPage from './pages/ScoreboardsPage.jsx';
import LiveTrackingPage from './pages/LiveTrackingPage.jsx';
// 2026-10-17 — Press Box (single-team stats module) and its public stats page.
import PressBoxGamesPage, { PressBoxIndex } from './pages/press-box/PressBoxGamesPage.jsx';
import PressBoxGamePage     from './pages/press-box/PressBoxGamePage.jsx';
import PressBoxSeasonPage   from './pages/press-box/PressBoxSeasonPage.jsx';
import PressBoxSettingsPage from './pages/press-box/PressBoxSettingsPage.jsx';
import PressBoxSetupPage    from './pages/press-box/PressBoxSetupPage.jsx';
import PressBoxRosterPage   from './pages/press-box/PressBoxRosterPage.jsx';
import PressBoxPublishPage  from './pages/press-box/PressBoxPublishPage.jsx';
import PressBoxStatsPage    from './pages/press-box/PressBoxStatsPage.jsx';
import PressBoxFamilyPreviewPage from './pages/press-box/PressBoxFamilyPreviewPage.jsx';
import PublicStatsPage, { PublicStatsGamePage } from './pages/public-stats/PublicStatsPage.jsx';
// 2026-06-10 — team media gallery (encrypted photos/videos of games).
import TeamMediaPage from './pages/TeamMediaPage.jsx';
import GameScoreboardPage from './pages/GameScoreboardPage.jsx';
// Apr 2026 (Tom_Notes E2) — coach-only money dashboard.
import FinancePage    from './pages/FinancePage.jsx';
import BudgetPage     from './pages/budget/BudgetPage.jsx';
import NotificationLogPage from './pages/NotificationLogPage.jsx';
import ProfilePage    from './pages/ProfilePage.jsx';
import ActivityPage   from './pages/ActivityPage.jsx';
import NotFoundPage   from './pages/NotFoundPage.jsx';
import AdminPage      from './pages/AdminPage.jsx';
import PaymentPage    from './pages/PaymentPage.jsx';

// v2.5.109 — Phase 2a of the League Module. Adds the league shell
// (list / dashboard / settings / members with the permission grid)
// at /leagues and /league/:leagueId/*. See LEAGUE_MODULE_PLAN.md §6.
// v2.5.110 — Phase 2b adds Venues + Divisions pages.
// v2.5.111 — Phase 2c adds Seasons page (with Start New Season
// wizard + team-invite flow) and the coach-side accept page.
// v2.5.112 — Phase 3 adds Players (pool / by-team / transfers) +
// CSV import.
// v2.5.113 — Phase 4 adds Schedule (generator wizard + games CRUD)
// and Officials (per-game assignment).
// v2.5.114 — Phase 5a adds the per-game Game Sheet page (lineup
// confirmation + live scoring of goals and penalties).
// v2.5.116 — Phase 6 adds the Standings + player leaderboard page.
import LeagueListPage      from './pages/league/LeagueListPage.jsx';
import LeagueDashboardPage from './pages/league/LeagueDashboardPage.jsx';
import LeagueSettingsPage  from './pages/league/LeagueSettingsPage.jsx';
import LeagueMembersPage   from './pages/league/LeagueMembersPage.jsx';
import LeagueVenuesPage    from './pages/league/LeagueVenuesPage.jsx';
import LeagueDivisionsPage from './pages/league/LeagueDivisionsPage.jsx';
import LeagueSeasonsPage   from './pages/league/LeagueSeasonsPage.jsx';
import LeaguePlayersPage   from './pages/league/LeaguePlayersPage.jsx';
import LeagueSchedulePage  from './pages/league/LeagueSchedulePage.jsx';
import LeagueOfficialsPage from './pages/league/LeagueOfficialsPage.jsx';
import LeagueGameSheetPage from './pages/league/LeagueGameSheetPage.jsx';
import LeagueStandingsPage from './pages/league/LeagueStandingsPage.jsx';
// v2.5.122 — Phase 8 of League Module. Per-league announcements
// feed (read for any member; compose for managers + admins with
// manage_announcements). Notification fan-out to all teams in
// the league happens server-side on POST.
import LeagueAnnouncementsPage from './pages/league/LeagueAnnouncementsPage.jsx';
// Phase B (league gap-closure, 2026-06-09) — cross-team coaches channel.
import LeagueChatPage from './pages/league/LeagueChatPage.jsx';
// Phase F (league gap-closure) — playoff brackets.
import LeaguePlayoffsPage from './pages/league/LeaguePlayoffsPage.jsx';
// Public pages overhaul P4 — public-site authoring (blog, coach
// profiles, public settings).
import LeaguePublicSitePage from './pages/league/LeaguePublicSitePage.jsx';
// v2.5.133 — Phase 9A. Registration admin (form builder + waivers).
import LeagueRegistrationPage from './pages/league/LeagueRegistrationPage.jsx';
import LeagueTeamInviteAcceptPage from './pages/league/LeagueTeamInviteAcceptPage.jsx';

// v2.5.121 — Phase 7 of the League Module. Anonymous public surface
// at /l/:slug/ with home / standings / schedule / leaders / team /
// game pages. Routes live OUTSIDE the PrivateRoute wrapper so
// unauthenticated visitors hit them; the layout doesn't pull in
// DeviceRouter or AppLayout (no app-shell baggage on shareable URLs).
import PublicLeagueLayout         from './pages/public-league/PublicLeagueLayout.jsx';
import PublicLeagueHomePage       from './pages/public-league/PublicLeagueHomePage.jsx';
import PublicLeagueStandingsPage  from './pages/public-league/PublicLeagueStandingsPage.jsx';
import PublicLeagueSchedulePage   from './pages/public-league/PublicLeagueSchedulePage.jsx';
import PublicLeagueLeaderboardPage from './pages/public-league/PublicLeagueLeaderboardPage.jsx';
import PublicLeagueTeamPage       from './pages/public-league/PublicLeagueTeamPage.jsx';
import PublicLeagueGamePage       from './pages/public-league/PublicLeagueGamePage.jsx';
// v2.5.134 — Phase 9B public registration form.
import PublicRegisterPage         from './pages/public-league/PublicRegisterPage.jsx';
// Phase E (league gap-closure) — guardian consent landing page.
import ConsentConfirmPage         from './pages/public-league/ConsentConfirmPage.jsx';
// Public pages overhaul P3 (2026-06-09) — news, directory, coaches,
// public playoff brackets.
import PublicLeagueNewsPage       from './pages/public-league/PublicLeagueNewsPage.jsx';
import PublicLeagueArticlePage    from './pages/public-league/PublicLeagueArticlePage.jsx';
import PublicLeagueTeamsPage      from './pages/public-league/PublicLeagueTeamsPage.jsx';
import PublicLeagueCoachesPage    from './pages/public-league/PublicLeagueCoachesPage.jsx';
import PublicLeaguePlayoffsPage   from './pages/public-league/PublicLeaguePlayoffsPage.jsx';

// ── Route guards ─────────────────────────────────────────────────
// v2.5.146 — onboarding gate. New users (zero teams + zero leagues
// + no pending invites + onboarding_completed_at IS NULL) get
// redirected to /welcome where the wizard runs. The shouldShow
// computation lives server-side; the result is cached in
// services/onboardingCache.js (module-scope) so we don't refetch
// on every navigation. Login/logout MUST call resetOnboardingCheck()
// to avoid cross-user pollution (see AuthContext).
import { fetchOnboardingState, getCachedOnboardingShouldShow } from './services/onboardingCache.js';

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  // Initialize from cache when available so re-mounts don't show a
  // double loading state. null means "haven't checked yet".
  const cachedInitial = getCachedOnboardingShouldShow();
  const [onboardingChecked, setOnboardingChecked] = React.useState(cachedInitial !== null);
  const [shouldShowWizard, setShouldShowWizard] = React.useState(cachedInitial === true);

  React.useEffect(() => {
    if (!user) return;
    if (getCachedOnboardingShouldShow() !== null) {
      // Cache already populated — keep our state in sync (handles
      // the case where another component triggered the fetch).
      setShouldShowWizard(getCachedOnboardingShouldShow() === true);
      setOnboardingChecked(true);
      return;
    }
    let cancelled = false;
    fetchOnboardingState().then(result => {
      if (cancelled) return;
      setShouldShowWizard(result);
      setOnboardingChecked(true);
    });
    return () => { cancelled = true; };
  }, [user]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="text-6xl mb-4" style={{ filter: 'drop-shadow(0 0 20px rgba(0,212,255,0.4))' }}>🏟</div>
        <div className="spinner mx-auto" />
        <p className="text-secondary text-sm mt-3">Loading…</p>
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;

  // Wait for the onboarding check on first paint, but never on
  // /welcome itself (avoid infinite-loop where the wizard page
  // triggers the gate that redirects to /welcome).
  if (!onboardingChecked && location.pathname !== '/welcome') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4" style={{ filter: 'drop-shadow(0 0 20px rgba(0,212,255,0.4))' }}>🏟</div>
          <div className="spinner mx-auto" />
          <p className="text-secondary text-sm mt-3">Loading…</p>
        </div>
      </div>
    );
  }

  // New-user redirect — once the wizard finishes and calls
  // resetOnboardingCheck(), subsequent navigation skips this branch
  // because the next fetch returns shouldShow=false.
  if (shouldShowWizard && location.pathname !== '/welcome') {
    return <Navigate to="/welcome" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <Navigate to="/dashboard" replace /> : children;
};

// Admin-only guard — redirects non-admins to dashboard
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user)             return <Navigate to="/login"     replace />;
  if (user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return children;
};

// ── Email verification page ───────────────────────────────────────
const VerifyEmailPage = () => {
  const [status, setStatus] = React.useState('verifying');
  // Fix #18: use useParams instead of manually splitting window.location.pathname,
  // which breaks if the app is ever served from a subpath.
  const { userId: uid, token: tok } = useParams();
  React.useEffect(() => {
    import('./services/api.js').then(({ default: api }) =>
      api.post('/auth/verify', { userId: uid, token: tok })
        .then(() => setStatus('success'))
        .catch(() => setStatus('error'))
    );
  }, [uid, tok]);
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="glass max-w-sm w-full p-8 text-center animate-slide-up">
        {status === 'verifying' && <><div className="spinner mx-auto mb-4" /><p className="text-secondary">Verifying…</p></>}
        {status === 'success'   && <><div className="text-5xl mb-4">✅</div><h2 className="text-xl font-bold mb-2">Email Verified!</h2><a href="/login" className="btn-ice mx-auto mt-4">Sign In</a></>}
        {status === 'error'     && <><div className="text-5xl mb-4">❌</div><h2 className="text-xl font-bold mb-2">Verification Failed</h2><p className="text-secondary text-sm">Link may have expired. Try resending from your profile.</p><a href="/login" className="btn-ghost mx-auto mt-4">Go to Login</a></>}
      </div>
    </div>
  );
};

// ── Email-change verification page (Apr 2026, Tom_Notes H1) ─────
// Sibling to VerifyEmailPage above, but for SELF-SERVE email
// changes initiated from Profile. The link in the user's new
// inbox lands here; we POST the token to the redeem endpoint and
// the backend swaps the address. On success the user can keep
// using the app — they're already authenticated. On failure we
// show a useful error (expired, collision, etc.) and a back-to-
// Profile link.
const VerifyEmailChangePage = () => {
  const [status, setStatus] = React.useState('verifying');
  const [errorMsg, setErrorMsg] = React.useState('');
  const { token: tok } = useParams();
  React.useEffect(() => {
    import('./services/api.js').then(({ default: api }) =>
      api.post('/auth/email-change/verify', { token: tok })
        .then(() => setStatus('success'))
        .catch(err => {
          setStatus('error');
          setErrorMsg(err?.response?.data?.error || 'Could not verify. The link may have expired.');
        })
    );
  }, [tok]);
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="glass max-w-sm w-full p-8 text-center animate-slide-up">
        {status === 'verifying' && (
          <><div className="spinner mx-auto mb-4" /><p className="text-secondary">Confirming your new email…</p></>
        )}
        {status === 'success' && (
          <>
            <div className="text-5xl mb-4">📬</div>
            <h2 className="text-xl font-bold mb-2">Email Updated</h2>
            <p className="text-secondary text-sm mb-4">
              Your new address is now active. Use it the next time you sign in.
            </p>
            <a href="/profile" className="btn-ice mx-auto mt-2">Back to Profile</a>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="text-5xl mb-4">❌</div>
            <h2 className="text-xl font-bold mb-2">Couldn't Verify</h2>
            <p className="text-secondary text-sm mb-4">{errorMsg}</p>
            <a href="/profile" className="btn-ghost mx-auto mt-2">Back to Profile</a>
          </>
        )}
      </div>
    </div>
  );
};


// ── Error Boundary — catches render crashes and shows friendly message ──
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, newVersion: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('App error:', error, info);
    // 2026-09-28 — a tab left open across a deploy keeps running the old
    // JavaScript until it reloads, and a crash there is often one the new
    // version already fixed (the league schedule page did exactly this
    // right after v2.5.300 went out). If the server now has a different
    // build than the one running here, say so and make Reload the answer.
    const running = import.meta.env.VITE_APP_VERSION;
    fetch('/version.json', { cache: 'no-store', credentials: 'omit' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        const v = j && typeof j.version === 'string' ? j.version : null;
        if (v && running && v !== running) this.setState({ newVersion: v });
      })
      .catch(() => { /* offline — the plain buttons still work */ });
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh', display: 'flex', alignItems: 'center',
          justifyContent: 'center', background: 'var(--bg)', padding: 24,
        }}>
          <div style={{ textAlign: 'center', maxWidth: 400 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🏟</div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 24, marginBottom: 8, color: 'var(--text)' }}>
              Something went wrong
            </h2>
            {this.state.newVersion ? (
              <p style={{ color: 'var(--text-2)', marginBottom: 24, fontSize: 14 }}>
                Game Day was just updated (v{this.state.newVersion}). This page was still running the
                previous version — reload to switch to the new one.
              </p>
            ) : (
              <p style={{ color: 'var(--text-2)', marginBottom: 24, fontSize: 14 }}>
                {this.state.error?.message || 'An unexpected error occurred.'}
              </p>
            )}
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 24px', borderRadius: 10, cursor: 'pointer',
                background: this.state.newVersion ? 'var(--ice)' : 'var(--ice-a12)',
                border: '1px solid var(--ice-a30)',
                color: this.state.newVersion ? '#000' : 'var(--ice)', fontWeight: 700, marginRight: 8, marginBottom: 8,
              }}>
              Reload
            </button>
            <button
              onClick={() => { this.setState({ hasError: false }); window.history.back(); }}
              style={{
                padding: '10px 24px', borderRadius: 10, cursor: 'pointer',
                background: 'var(--ice-a12)', border: '1px solid var(--ice-a30)',
                color: 'var(--ice)', fontWeight: 600, marginRight: 8,
              }}>
              Go Back
            </button>
            <button
              onClick={() => window.location.href = '/dashboard'}
              style={{
                padding: '10px 24px', borderRadius: 10, cursor: 'pointer',
                background: 'var(--surface-2)', border: '1px solid var(--border)',
                color: 'var(--text-2)', fontWeight: 600,
              }}>
              Dashboard
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── App ───────────────────────────────────────────────────────────
// Native-app bridge: registers deep-link routing + push notifications when
// running inside the Capacitor app. Renders nothing; no-ops on the web.
// Lives inside BrowserRouter (for useNavigate) and AuthProvider (for useAuth).
function MobileBridge() {
  const navigate = useNavigate();
  const { user } = useAuth();
  React.useEffect(() => {
    let cleanup = () => {};
    initDeepLinks(navigate).then((fn) => { if (fn) cleanup = fn; });
    return () => cleanup();
  }, [navigate]);
  React.useEffect(() => {
    if (user) initPushNotifications(navigate);
  }, [user, navigate]);
  return null;
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <ThemeProvider>
    <BrowserRouter>
      <PWAProvider>
      <AuthProvider>
        <MobileBridge />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              color: 'var(--text)',
              fontFamily: 'var(--font-body)',
              fontSize: '14px',
            },
            success: { icon: '🏟' },
            error:   { icon: '🚨' },
          }}
        />

        <ErrorBoundary>
        <Routes>
          {/* ── Public ── */}
          <Route path="/login"           element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register"        element={<PublicRoute><RegisterPage /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
          <Route path="/reset-password/:userId/:token" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
          <Route path="/verify/:userId/:token"         element={<VerifyEmailPage />} />
          {/* Apr 2026 (Tom_Notes H1) — self-serve email-change link target.
              Authenticated route — the user is presumably already signed
              in when they click the link from their new inbox; if not,
              the public form gracefully shows the redeemed message and
              kicks back to login. */}
          <Route path="/verify-email-change/:token"    element={<VerifyEmailChangePage />} />
          <Route path="/invite/:token"                 element={<InviteAcceptPage />} />
          <Route path="/join/:token"                   element={<InviteAcceptPage />} />
          {/* v2.5.97 — landing page after a one-click spare invite
              email RSVP. No auth required. The backend redirects
              here with ?action=in|out&status=ok|... params. */}
          <Route path="/rsvp/result"                   element={<RsvpResultPage />} />

          {/* v2.5.121 — Phase 7 league public surface. Anonymous;
              outside the PrivateRoute wrapper so visitors without a
              JWT can hit shareable URLs. The Layout fetches league
              meta once and provides it to children via outlet
              context; each child page uses publicApi (no JWT) for
              its own data calls. */}
          <Route path="/l/:slug" element={<PublicLeagueLayout />}>
            <Route index             element={<PublicLeagueHomePage />} />
            <Route path="standings"  element={<PublicLeagueStandingsPage />} />
            <Route path="schedule"   element={<PublicLeagueSchedulePage />} />
            <Route path="leaders"    element={<PublicLeagueLeaderboardPage />} />
            <Route path="team/:teamId" element={<PublicLeagueTeamPage />} />
            <Route path="game/:gameId" element={<PublicLeagueGamePage />} />
            {/* Public pages overhaul P3 (2026-06-09) — news/blog,
                team directory, coach profiles, playoff brackets. */}
            <Route path="news"           element={<PublicLeagueNewsPage />} />
            <Route path="news/:postId"   element={<PublicLeagueArticlePage />} />
            <Route path="teams"          element={<PublicLeagueTeamsPage />} />
            <Route path="coaches"        element={<PublicLeagueCoachesPage />} />
            <Route path="playoffs"       element={<PublicLeaguePlayoffsPage />} />
            {/* v2.5.134 — Phase 9B public registration form. */}
            <Route path="register"                  element={<PublicRegisterPage />} />
            <Route path="register/:divisionSlug"    element={<PublicRegisterPage />} />
          </Route>

          {/* 2026-10-17 — Press Box public stats page. Anonymous; the
              token is the key; players shown as the coach chose. */}
          <Route path="/stats/:token" element={<PublicStatsPage />} />
          <Route path="/stats/:token/game/:gameId" element={<PublicStatsGamePage />} />

          {/* Phase E (league gap-closure) — guardian consent landing
              page from the email link. Anonymous; the token is the
              credential. */}
          <Route path="/consent/:token" element={<ConsentConfirmPage />} />

          {/* v2.5.146 — Onboarding wizard. Full-screen (no
              layout shell). PrivateRoute applies so the user must
              be logged in, but DeviceRouter wraps the dashboard
              shell which we don't want here — the wizard owns
              its entire viewport. */}
          <Route path="/welcome" element={<PrivateRoute><OnboardingWizardPage /></PrivateRoute>} />

          {/* ── Protected (inside layout shell) ── */}
          <Route path="/" element={<PrivateRoute><DeviceRouter /></PrivateRoute>}>
            <Route index                                element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard"                     element={<DashboardPage />} />
            <Route path="game/:gameId"                  element={<GamePage />} />
            <Route path="profile"                       element={<ProfilePage />} />
            <Route path="activity"                      element={<ActivityPage />} />

            {/* Team-scoped routes */}
            {/* Apr 2026 — Team Settings broken out into URL-addressable
                sub-pages. The old /team/:teamId URL now redirects to
                the first sub-page (Team Info) so existing bookmarks
                still land somewhere sensible. The TeamPage itself
                reads the :section URL param and renders only the
                matching card; sidebar nav (in each layout) shows
                the sub-items when the user is on any settings route. */}
            <Route path="team/:teamId" element={<Navigate to="settings/team-info" replace />} />
            <Route path="team/:teamId/settings"            element={<Navigate to="team-info" replace />} />
            <Route path="team/:teamId/settings/:section"   element={<TeamPage />} />
            <Route path="team/:teamId/players"          element={<PlayersPage />} />
            <Route path="team/:teamId/players/import"   element={<ImportPlayersPage />} />
            <Route path="team/:teamId/seasons"          element={<SeasonsPage />} />
            <Route path="team/:teamId/games"            element={<SchedulePage />} />
            <Route path="team/:teamId/games/import"     element={<ImportSchedulePage />} />
            <Route path="team/:teamId/chat"             element={<ChatPage />} />
            <Route path="team/:teamId/stats"            element={<StatsPage />} />
            {/* May 4 2026 (v2.5.70) — player-scoped stats. Players land
                here from the new "Stats" sidebar entry that replaced
                "Team" in the non-coach nav. Coach Stats is unchanged
                at /stats. */}
            <Route path="team/:teamId/my-stats"         element={<MyStatsPage />} />
            {/* Apr 30 2026 — Scoreboards menu page. Lists every
                game with its score; foundation for season recap +
                future AI analysis. The page itself renders an
                "off" notice if the team has scoreboard_enabled =
                false (defense-in-depth on top of the menu hide). */}
            <Route path="team/:teamId/scoreboards"      element={<ScoreboardsPage />} />
            {/* 2026-09-19 (Phase 7A/7B) - bench live tracking + recorder. */}
            <Route path="team/:teamId/game/:gameId/live" element={<LiveTrackingPage />} />
            {/* 2026-10-17 — Press Box: games + uploads, one game (AI analysis,
                held WhatsApp updates, stats editor, play-by-play), season
                leaders, settings (AI, public page, WhatsApp). Server-gated by
                the pressBox flag; the nav entry hides without it. */}
            <Route path="press-box"                        element={<PressBoxIndex />} />
            {/* 2026-10-18 — Press Box as a product: its own onboarding (no
                settings copied, no payments) and the account-less roster. */}
            <Route path="press-box/setup"                  element={<PressBoxSetupPage />} />
            <Route path="press-box/:teamId"                element={<PressBoxGamesPage />} />
            <Route path="press-box/:teamId/roster"         element={<PressBoxRosterPage />} />
            <Route path="press-box/:teamId/publish"        element={<PressBoxPublishPage />} />
            <Route path="press-box/:teamId/stats"          element={<PressBoxStatsPage />} />
            <Route path="press-box/:teamId/season"         element={<PressBoxSeasonPage />} />
            <Route path="press-box/:teamId/settings"       element={<PressBoxSettingsPage />} />
            <Route path="press-box/:teamId/game/:gameId"   element={<PressBoxGamePage />} />
            <Route path="press-box/:teamId/game/:gameId/preview" element={<PressBoxFamilyPreviewPage />} />
            {/* 2026-06-10 — team media gallery (photos/videos of games,
                encrypted at rest). ?game=<id> filters to one game. */}
            <Route path="team/:teamId/media"            element={<TeamMediaPage />} />
            {/* May 2 2026 — per-game live scoreboard at its own URL.
                Was embedded in DashboardPage hero + GamePage mid-scroll;
                moved out per coach request to give it dedicated space
                + one consistent location to bookmark. Linked from both
                pages via "Live Scoreboard →" buttons. */}
            <Route path="team/:teamId/games/:gameId/scoreboard" element={<GameScoreboardPage />} />
            {/* Apr 2026 (Tom_Notes E2) — Finance tab. Page itself
                is coach-gated client-side via useTeamRole + the
                backend re-checks via requireTeamRole, so a player
                hand-typing the URL gets a polite "coach only" card. */}
            <Route path="team/:teamId/finance"          element={<FinancePage />} />
            <Route path="team/:teamId/budget"           element={<BudgetPage />} />
            <Route path="team/:teamId/notification-log" element={<NotificationLogPage />} />

            {/* Admin-only */}
            <Route path="admin" element={<AdminRoute><AdminPage /></AdminRoute>} />

            {/* ── League Module (Phase 2a) ──
                /leagues               — list user's leagues + create new
                /league/:id            — redirect to /league/:id/dashboard
                /league/:id/dashboard  — overview
                /league/:id/settings   — league settings (manage_league_settings)
                /league/:id/members    — member roster, invite, permission grid
                The Phase 2b/2c sections (venues, divisions, seasons,
                schedule, officials, public page) will register here as
                they ship. Each page does its own permission check via
                useLeagueMembership; the backend re-checks every API call. */}
            <Route path="leagues"                     element={<LeagueListPage />} />
            <Route path="league/:leagueId"            element={<Navigate to="dashboard" replace />} />
            <Route path="league/:leagueId/dashboard"  element={<LeagueDashboardPage />} />
            <Route path="league/:leagueId/settings"   element={<LeagueSettingsPage />} />
            <Route path="league/:leagueId/members"    element={<LeagueMembersPage />} />
            <Route path="league/:leagueId/venues"     element={<LeagueVenuesPage />} />
            <Route path="league/:leagueId/divisions"  element={<LeagueDivisionsPage />} />
            <Route path="league/:leagueId/seasons"    element={<LeagueSeasonsPage />} />
            <Route path="league/:leagueId/players"    element={<LeaguePlayersPage />} />
            <Route path="league/:leagueId/schedule"   element={<LeagueSchedulePage />} />
            <Route path="league/:leagueId/officials"  element={<LeagueOfficialsPage />} />
            <Route path="league/:leagueId/standings"  element={<LeagueStandingsPage />} />
            <Route path="league/:leagueId/announcements" element={<LeagueAnnouncementsPage />} />
            {/* Phase B (league gap-closure) — cross-team coaches channel. */}
            <Route path="league/:leagueId/chat"          element={<LeagueChatPage />} />
            {/* Phase F — playoff brackets (league gap-closure). */}
            <Route path="league/:leagueId/playoffs"      element={<LeaguePlayoffsPage />} />
            {/* P4 — public-site authoring. Also reachable by team
                coaches who aren't league members (via the "Team news"
                pill on their team chat page). */}
            <Route path="league/:leagueId/public-site"   element={<LeaguePublicSitePage />} />
            {/* v2.5.133 — Phase 9A. Registration admin (config +
                form builder + waiver sections). Public form
                arrives in 9B. */}
            <Route path="league/:leagueId/registration" element={<LeagueRegistrationPage />} />
            <Route path="league/:leagueId/game/:gameId" element={<LeagueGameSheetPage />} />
            {/* Coach-side magic-link landing — the user is authenticated
                (the api interceptor forces login first) but they're
                not yet in the league. The token IS the authorization. */}
            <Route path="league-invite/:token"        element={<LeagueTeamInviteAcceptPage />} />

            {/* Payment pages */}
            <Route path="payment/:page"          element={<PaymentPage />} />
            <Route path="payment/:page/:teamId"  element={<PaymentPage />} />
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </ErrorBoundary>
      </AuthProvider>
      </PWAProvider>
    </BrowserRouter>
    </ThemeProvider>
  </React.StrictMode>
);

// Register service worker — skip in Capacitor native context where
// WKWebView (iOS) doesn't support SW and Android WebView support is limited.
const isCapacitorNative = !!(window.Capacitor?.isNativePlatform?.() || window.Capacitor?.isNative);
if ('serviceWorker' in navigator && !isCapacitorNative) {
  navigator.serviceWorker.register('/sw.js').then(reg => {
    // Check for updates periodically
    setInterval(() => reg.update(), 60 * 60 * 1000); // every hour
  }).catch(console.error);

  // When a new SW activates, dispatch a custom event so the app can
  // show a "refresh available" toast. Do NOT call window.location.reload()
  // here — doing so races with React's render cycle and causes a blank page.
  let swUpdatePending = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!swUpdatePending) {
      swUpdatePending = true;
      setTimeout(() => window.dispatchEvent(new CustomEvent('gameday:sw-updated')), 500);
    }
  });
}
